import { Injectable } from '@angular/core';
import { AxialRotationEnum, BridgeResult, NextRayMeasuresDeformityAP, NextRayMeasuresDeformityLT, SelectedApexMech, StateTypes, ViewStateType, ViewType } from '@ortho-next/nextray-core';
import { BehaviorSubject, Observable, filter } from 'rxjs';
import { BaseEvent, EventListener } from 'three';
import { Bridge, BridgeActions, BridgeMessages } from '../../nextray/Core/Bridge';
import { AppModel, BOM, LayerElements, NextrayMeasuresEoCMech, PlateOrientationType } from '../../nextray/Models/AppModel';
import { OsteotomyCut, StateDescription, WorkflowRunning } from '../../nextray/States/State';
import { PrintResult } from '../../nextray/Utils/PrintUtils';
import { AnatomicalSideEnum, BoneTypeEnum, Plan, Plate } from '../core';
import { CaseService } from './case.service';
import { MainCanvasLoaderService } from './main-canvas-loader.service';
import { ModelService } from './model.service';

type BridgeEvents = BridgeResult<BridgeMessages>;


/**
* This service handles the canvas data.
*/
@Injectable({ providedIn: 'root' })
export class CanvasService {

	currentCase?: Plan;

	private _model?: AppModel;
	private _bridge?: Bridge;

	private _hasAP: boolean = false;
	private _hasLT: boolean = false;

	private _isLongLeg: boolean = false;
	private _isFemur: boolean = false;
	private _isTibia: boolean = false;

	private _isLeft: boolean = false;
	private _isRight: boolean = false;

	private readonly _drawerClosure: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private readonly caseSrv: CaseService,
		private readonly modelSrv: ModelService,
		private readonly loaderSrv: MainCanvasLoaderService
	) {
		this.loaderSrv.canvas3D.subscribe(c => {
			this._model = c.model;
			this._bridge = c.bridge;
		});
		this.caseSrv.getCurrentCase().subscribe(c => {
			this.updateImgStatus(c.apImageGuid, c.ltImageGuid);
			this.updateBoneStatus(c.boneType);
			this.updateSideStatus(c.side);
			this.currentCase = c;
		});
	}

	/**
	* Check if current case has AP image.
	*/
	get hasAP(): boolean {
		return this._hasAP;
	}
	/**
	* Check if current case has Lateral image.
	*/
	get hasLT(): boolean {
		return this._hasLT;
	}
	/**
	* Check if current case has Long Leg bone type.
	*/
	get isLongLeg(): boolean {
		return this._isLongLeg;
	}
	/**
	* Check if current case has Femur bone type.
	*/
	get isFemur(): boolean {
		return this._isFemur;
	}
	/**
	* Check if current case has Tibia bone type.
	*/
	get isTibia(): boolean {
		return this._isTibia;
	}
	/**
	* Check if current case has Left bone side.
	*/
	get isLeft(): boolean {
		return this._isLeft;
	}
	/**
	* Check if current case has Right bone side.
	*/
	get isRight(): boolean {
		return this._isRight;
	}
	/**
	* Get current state description.
	*/
	get stateDescription(): StateDescription | undefined {
		return this._model?.stateDescription;
	}
	/**
	* Get current state type.
	*/
	get stateType(): StateTypes | undefined {
		return this._model?.stateType;
	}
	/**
	* Get current view state, i.e AP, Lateral or Multiple.
	*/
	get activeViewState(): ViewStateType | undefined {
		return this._model?.activeViewState;
	}

	/**
	* Get current active view, i.e AP or Lateral.
	*/
	get activeView(): ViewType | undefined {
		return this._model?.activeView;
	}
	/**
	* Get images to create print report.
	*/
	get printResult(): PrintResult | undefined {
		return this._model?.printResult;
	}
	/**
	* Get image for magnifier.
	*/
	get magnifier(): string | undefined {
		return this._model?.magnifier;
	}
	/**
	* Get current deformity measures of mechanical axis in AP View.
	*/
	get defAPMeasures(): NextRayMeasuresDeformityAP | undefined {
		return this.isDefAPMeasuresVisible() ? this._model?.measures : undefined;
	}

	private isDefAPMeasuresVisible(): boolean {
		return !!this.stateType && this.stateType >= StateTypes.deformityAnalysis && this.isAPMechanicalAxisInserted && !(this.activeView === ViewType.AP && this.stateDescription?.isWorkflowRunning === WorkflowRunning.insertMechanicalAxis && this.stateDescription.isPointsPositioningRunning);
	}
	/**
	* Get current deformity measures of mechanical axis in Lateral View.
	*/
	get defLTMeasures(): NextRayMeasuresDeformityLT | undefined {
		return this.isDefLTMeasuresVisible() ? this._model?.measures : undefined;
	}

	private isDefLTMeasuresVisible(): boolean {
		return !!this.stateType && this.stateType >= StateTypes.deformityAnalysis && this.isLTMechanicalAxisInserted && !(this.activeView === ViewType.LT && this.stateDescription?.isWorkflowRunning === WorkflowRunning.insertMechanicalAxis && this.stateDescription.isPointsPositioningRunning);
	}

	/**
	* Get current deformity measures of contralateral axis.
	*/
	get defCLMeasures(): NextRayMeasuresDeformityAP | undefined {
		return this.isDefCLMeasuresVisible() ? this._model?.measures : undefined;
	}

	private isDefCLMeasuresVisible(): boolean {
		return !!this.stateType && this.stateType >= StateTypes.deformityAnalysis && this.isContralateralAxisInserted && !(this.activeView === ViewType.AP && this.stateDescription?.isWorkflowRunning === WorkflowRunning.insertContralateral && this.stateDescription.isPointsPositioningRunning);
	}

	/**
	* Get current correction measures.
	*/
	get eocMeasures(): NextrayMeasuresEoCMech | undefined {
		return this.stateType && this.stateType >= StateTypes.EOC && this.isEocCut ? this._model?.measures : undefined;
	}
	/**
	* Get scale factor of AP image.
	*/
	get scaleFactorAP(): number | undefined {
		return this._model?.scaleFactorAP;
	}
	/**
	* Get scale factor of Lateral image.
	*/
	get scaleFactorLT(): number | undefined {
		return this._model?.scaleFactorLT;
	}
	/**
	* Get current layers state.
	*/
	get layers(): LayerElements | undefined {
		return this._model?.layers;
	}
	/**
	* Get current layers values.
	*/
	get layersValue(): LayerElements | undefined {
		return this._model?.layersValue;
	}
	/**
	* Check if layers component is visible.
	*/
	get layersVisible(): boolean {
		return !!this._model?.layersVisible;
	}
	/**
	* Check if mechanical axis is inserted in AP.
	*/
	get isAPMechanicalAxisInserted(): boolean {
		return !!this._model?.isAPMechanicalAxisInserted;
	}
	/**
	* Check if mechanical axis is inserted in Lateral.
	*/
	get isLTMechanicalAxisInserted(): boolean {
		return !!this._model?.isLTMechanicalAxisInserted;
	}
	/**
	* Check if contralateral axis is inserted.
	*/
	get isContralateralAxisInserted(): boolean {
		return !!this._model?.isContralateralAxisInserted;
	}
	/**
	* Check if eoc cut is active.
	*/
	get isEocCut(): boolean {
		return !!this._model?.eocCut;
	}
	/**
	* Get eoc cut type.
	*/
	get osteotomyCut(): OsteotomyCut | undefined {
		return this._model?.osteotomyCut;
	}
	/**
	* Get selected apex.
	*/
	get selectedApex(): SelectedApexMech | undefined {
		return this._model?.selectedApex;
	}
	/**
	* Check if plate is inserted.
	*/
	get isPlateInserted(): boolean {
		return !!this._model?.isPlateInserted;
	}
	/**
	* Get plate orientation type.
	*/
	get plateOrientation(): PlateOrientationType | undefined {
		return this._model?.plateOrientation;
	}
	/**
	* Get plate.
	*/
	get plate(): Plate | undefined {
		return this._model?.plate?.plate;
	}
	/**
	* Get JPS Extended status.
	*/
	get hasJPSExtended(): boolean {
		return !!this._model?.plate?.hasJPSExtended;
	}
	/**
	* Check if plate is loading in AP view.
	*/
	get isAPPlateLoading(): boolean {
		return !!this._model?.isAPPlateLoading;
	}
	/**
	* Check if plate is loading in Lateral view.
	*/
	get isLTPlateLoading(): boolean {
		return !!this._model?.isLTPlateLoading;
	}
	/**
	 * Get screw list for BOM.
	 */
	get screwsBom(): BOM | undefined {
		return this._model?.screwsBom;
	}
	/**
	* Get axial roatation value of femur.
	*/
	get def_femur_axialRotation(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_femur_axialRotation : undefined;
	}
	/**
	* Get axial roatation sign of femur.
	*/
	get def_femur_axialRotationSign(): AxialRotationEnum | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_femur_axialRotationSign : undefined;
	}
	/**
	* Get axial roatation value of tibia.
	*/
	get def_tibia_axialRotation(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_tibia_axialRotation : undefined;
	}
	/**
	* Get axial roatation sign of tibia.
	*/
	get def_tibia_axialRotationSign(): AxialRotationEnum | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_tibia_axialRotationSign : undefined;
	}
	/**
	* Get standard angle of LPFA.
	*/
	get def_standardAngles_LPFA(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_standardAngles_LPFA : undefined;
	}
	/**
	* Get standard angle of mLDFA.
	*/
	get def_standardAngles_mLDFA(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_standardAngles_mLDFA : undefined;
	}
	/**
	* Get standard angle of MPTA.
	*/
	get def_standardAngles_MPTA(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_standardAngles_MPTA : undefined;
	}
	/**
	* Get standard angle of LDTA.
	*/
	get def_standardAngles_LDTA(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_standardAngles_LDTA : undefined;
	}
	/**
	* Get standard angle of PPTA.
	*/
	get def_standardAngles_PPTA(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_standardAngles_PPTA : undefined;
	}
	/**
	* Get standard angle of ADTA.
	*/
	get def_standardAngles_ADTA(): number | undefined {
		return this.stateType && this.stateType >= StateTypes.deformityAnalysis ? this._model?.def_standardAngles_ADTA : undefined;
	}
	/**
	 * Cortex Osteotomy level.
	 */
	get print_osteotomyLevel(): number | undefined {
		return this._model?.print_osteotomyLevel;
	}
	/**
	 * Cortex Wire insertion point.
	 */
	get print_cortexWire(): number | undefined {
		return this._model?.print_cortexWire;
	}
	/**
	 * Wire Insertion Angle.
	 */
	get print_wireAngle(): number | undefined {
		return this._model?.print_wireAngle;
	}
	/**
	 * Get brightness of AP image.
	 */
	public get brightnessAP(): number | undefined {
		return this._model?.brightnessAP;
	}
	/**
	 * Get brightness of Lateral image.
	 */
	public get brightnessLT(): number | undefined {
		return this._model?.brightnessLT;
	}
	/**
	 * Get contrast of AP image.
	 */
	public get contrastAP(): number | undefined {
		return this._model?.contrastAP;
	}
	/**
	 * Get contrast of Lateral image.
	 */
	public get contrastLT(): number | undefined {
		return this._model?.contrastLT;
	}
	/**
	 * Gets bone type of Lateral view.
	 */
	public get boneTypeLT(): BoneTypeEnum | undefined {
		return this._model?.boneTypeLT as BoneTypeEnum;
	}
	/**
	 * Indicates if measurement tool is selected in AP view.
	 */
	public get isMeasurementToolSelectedAP(): boolean {
		return !!this._model?.isMeasurementToolSelectedAP;
	}
	/**
	 * Indicates if measurement tool is selected in LT view.
	 */
	public get isMeasurementToolSelectedLT(): boolean {
		return !!this._model?.isMeasurementToolSelectedLT;
	}
	/**
	 * Check if undo action is enabled.
	 */
	public get isUndoEnabled(): boolean {
		return !!this._model?.hasUndo;
	}
	/**
	 * Check if redo action is enabled.
	 */
	public get isRedoEnabled(): boolean {
		return !!this._model?.hasRedo;
	}

	/**
	* Dispatch an event or action.
	*/
	dispatch<K extends keyof BridgeActions>(type: K, args?: BridgeActions[K]): void {
		this._bridge?.mapEvent(type, args!);
	}

	/**
	* Add event listener on bridge.
	*/
	addEventListener<T extends keyof BridgeEvents>(type: T, listener: EventListener<BridgeEvents[T], T, Bridge>): void {
		this._bridge?.addEventListener(type, listener);
	}

	/**
	* Check if has an event listener on bridge.
	*/
	hasEventListener<T extends keyof BridgeEvents>(type: T, listener: EventListener<BridgeEvents[T], T, Bridge>): boolean {
		return !!this._bridge?.hasEventListener(type, listener);
	}

	/**
	* Remove an event listener on bridge.
	*/
	removeEventListener<T extends keyof BridgeEvents>(type: T, listener: EventListener<BridgeEvents[T], T, Bridge>): void {
		this._bridge?.removeEventListener(type, listener);
	}

	/**
	* Dispatch an event listener on bridge.
	*/
	dispatchEvent<T extends keyof BridgeEvents>(event: BaseEvent<T> & BridgeEvents[T]): void {
		this._bridge?.dispatchEvent(event);
	}

	/**
	* Save current state.
	*/
	saveState(): void {
		this.modelSrv.persistModel(this.currentCase!.id, this.currentCase!.userGuid).subscribe();
	}

	/**
	 * Close drawer.
	 */
	closeDrawer(): void {
		this._drawerClosure.next(true);
	}

	/**
	 * Drawer closure event.
	 */
	drawerClosure(): Observable<boolean> {
		return this._drawerClosure.asObservable().pipe(filter(ev => !!ev));
	}

	private updateBoneStatus(boneType: BoneTypeEnum): void {
		this._isLongLeg = boneType === BoneTypeEnum.LongLeg;
		this._isFemur = boneType === BoneTypeEnum.Femur;
		this._isTibia = boneType === BoneTypeEnum.Tibia;
	}

	private updateImgStatus(apImg?: string, ltImg?: string) {
		this._hasAP = !!apImg && apImg !== '';
		this._hasLT = !!ltImg && ltImg !== '';
	}

	private updateSideStatus(side: AnatomicalSideEnum) {
		this._isLeft = side === AnatomicalSideEnum.Left;
		this._isRight = side === AnatomicalSideEnum.Right;
	}
}

export enum BoneImageEnum {
	Left_LongLeg_AP = 'images/bones/bones_left_longleg_AP.gif',
	Left_LongLeg_LT = 'images/bones/bones_left_longleg_LATERAL.gif',
	Right_LongLeg_AP = 'images/bones/bones_right_longleg_AP.gif',
	Right_LongLeg_LT = 'images/bones/bones_right_longleg_LATERAL.gif',

	Left_Femur_AP = 'images/bones/bones_femore_AP_LEFT.gif',
	Right_Femur_AP = 'images/bones/bones_femore_AP_RIGHT.gif',
	Left_Femur_LT = 'images/bones/bones_femore_LT_LEFT.gif',
	Right_Femur_LT = 'images/bones/bones_femore_LT_RIGHT.gif',

	Left_Tibia_AP = 'images/bones/bones_tibia_AP_LEFT.gif',
	Right_Tibia_AP = 'images/bones/bones_tibia_AP_RIGHT.gif',
	Left_Tibia_LT = 'images/bones/bones_tibia_LT_LEFT.gif',
	Right_Tibia_LT = 'images/bones/bones_tibia_LT_RIGHT.gif',
}
